import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { BADGES, LEGACY_BADGE_ID_MAP } from '../lib/badges.js';

/* ══════════════════════════════════════════════════════
   GAMIFICATION STORE — Streaks, badges, challenges.

   Stage data + computation now live in src/lib/steps.js
   and src/hooks/useCurrentStage.js (single source of
   truth, derived from finance state — never persisted).
   The previous `currentStage` / `advanceStage` / dead
   `JOURNEY_STAGES` constant were removed in v2.
   ══════════════════════════════════════════════════════ */

export const useGamificationStore = create(
  persist(
    (set, get) => ({
      // ── Streaks ──
      streak: 0,
      lastLogDate: null,
      longestStreak: 0,

      // Recent streak event for the witness layer (#4 break, #5 comeback).
      // Written by updateStreak() when conditions are met. Witness rules at
      // read time should ignore events older than ~7 days. Never auto-cleared.
      streakLastEvent: null,

      updateStreak: () => {
        const today = new Date().toISOString().slice(0, 10);
        const { lastLogDate, streak, longestStreak } = get();
        if (lastLogDate === today) return;
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().slice(0, 10);
        const newStreak = lastLogDate === yesterdayStr ? streak + 1 : 1;

        let event = null;
        if (lastLogDate) {
          const daysAbsent = Math.round(
            (new Date(today) - new Date(lastLogDate)) / 86400000,
          );
          if (daysAbsent >= 14) {
            event = { kind: 'comeback', at: today, priorStreak: streak, daysAbsent };
          } else if (streak >= 3 && newStreak === 1) {
            event = { kind: 'break', at: today, priorStreak: streak };
          }
        }

        set({
          streak: newStreak,
          lastLogDate: today,
          longestStreak: Math.max(longestStreak, newStreak),
          ...(event ? { streakLastEvent: event } : {}),
        });
      },

      // ── No-Spend Days ──
      noSpendDays: {},
      addNoSpendDay: (monthKey, date) => set(state => {
        const existing = state.noSpendDays[monthKey] || [];
        if (existing.includes(date)) return state;
        return { noSpendDays: { ...state.noSpendDays, [monthKey]: [...existing, date] } };
      }),

      // ── Badges ──
      availableBadges: BADGES,
      earnedBadges: [],
      newBadge: null,

      earnBadge: (badgeId) => {
        const { earnedBadges, availableBadges } = get();
        if (earnedBadges.some(b => b.id === badgeId)) return;
        const badge = availableBadges.find(b => b.id === badgeId);
        if (!badge) return;
        const earned = { ...badge, earnedAt: new Date().toISOString() };
        set({ earnedBadges: [...earnedBadges, earned], newBadge: earned });
      },
      clearNewBadge: () => set({ newBadge: null }),

      // ── Challenges ──
      activeChallenges: [],
      completedChallenges: [],
      setActiveChallenges: (activeChallenges) => set({ activeChallenges }),
      completeChallenge: (id) => set(state => {
        const challenge = state.activeChallenges.find(c => c.id === id);
        if (!challenge) return state;
        return {
          activeChallenges: state.activeChallenges.filter(c => c.id !== id),
          completedChallenges: [...state.completedChallenges, { ...challenge, completedAt: new Date().toISOString() }],
        };
      }),

      // ── Leaderboard ──
      joinedLeaderboard: false,
      setJoinedLeaderboard: (joinedLeaderboard) => set({ joinedLeaderboard }),

      // ── Letter loop (week-3) ──
      // lastSeenStage tracks the last stage value the LetterController
      // observed for this user. When useCurrentStage().currentStage > this
      // value, a transition has occurred — opportunity to prompt for a
      // letter. After handling (write OR skip OR defer), the controller
      // bumps this. Initial 1 (the floor stage everyone starts on).
      lastSeenStage: 1,
      setLastSeenStage: (lastSeenStage) => set({ lastSeenStage }),

      // skippedLetterStages: stage numbers the user permanently dismissed
      // (X'd the dashboard card). Once skipped, that stage's prompt never
      // re-appears. Different from "Not now" — that defers within session.
      skippedLetterStages: [],
      skipLetterStage: (stage) => set(state => (
        state.skippedLetterStages.includes(stage)
          ? state
          : { skippedLetterStages: [...state.skippedLetterStages, stage] }
      )),

      // deferredLetterStage: NOT persisted (excluded from partialize). Set
      // when the user clicks "Not now" on the modal; cleared on seal,
      // permanent skip, or session end. The Dashboard card subscribes to
      // this so the prompt downgrades from interrupt → quiet card after
      // the user defers, without the controller and card needing a shared
      // parent.
      deferredLetterStage: null,
      setDeferredLetterStage: (stage) => set({ deferredLetterStage: stage }),
    }),
    {
      name: 'karat-gamification',
      // v2 (week-4): drop dead stage fields, zero stale streak, init
      //              streakLastEvent.
      // v3 (week-4 phase 4.3): remap legacy badge ids to canonical ids;
      //              drop unmapped earned badges (no longer in catalogue).
      // v4 (week-3 phase 3b): add lastSeenStage + skippedLetterStages for
      //              the past-self letter loop.
      version: 4,
      migrate: (persisted, fromVersion) => {
        if (fromVersion < 2) {
          // Streak hygiene: zero out stale streak when lastLogDate was never
          // written (the period between ClarityBudget retirement and the
          // wiring fix). Preserve longestStreak as the all-time peak.
          if (persisted.lastLogDate == null && (persisted.streak || 0) > 0) {
            persisted.longestStreak = Math.max(
              persisted.longestStreak || 0,
              persisted.streak || 0,
            );
            persisted.streak = 0;
          }
          persisted.streakLastEvent = null;
          // Stage state moved out of the store. Drop persisted fields so the
          // derived selector (useCurrentStage) is unambiguously canonical.
          delete persisted.currentStage;
          delete persisted.stageProgress;
          delete persisted.journeyStages;
        }
        if (fromVersion < 3) {
          // Remap legacy badge ids to canonical ids; drop unmapped.
          const valid = new Set(BADGES.map(b => b.id));
          const seen = new Set();
          persisted.earnedBadges = (persisted.earnedBadges || [])
            .map(b => {
              const newId = LEGACY_BADGE_ID_MAP[b.id] || b.id;
              if (!valid.has(newId) || seen.has(newId)) return null;
              seen.add(newId);
              const meta = BADGES.find(x => x.id === newId);
              return { ...b, id: newId, name: meta.name, desc: meta.desc, icon: meta.icon };
            })
            .filter(Boolean);
        }
        if (fromVersion < 4) {
          // Initialize letter-loop fields. lastSeenStage starts at 1 — a
          // user already past stage 1 will only see prompts at their NEXT
          // transition, never retroactive. Matches the "next unwritten
          // stage above current" rule documented in lib/letters.js.
          if (persisted.lastSeenStage == null) persisted.lastSeenStage = 1;
          if (!Array.isArray(persisted.skippedLetterStages)) {
            persisted.skippedLetterStages = [];
          }
        }
        return persisted;
      },
      partialize: (state) => ({
        streak: state.streak,
        lastLogDate: state.lastLogDate,
        longestStreak: state.longestStreak,
        streakLastEvent: state.streakLastEvent,
        noSpendDays: state.noSpendDays,
        earnedBadges: state.earnedBadges,
        activeChallenges: state.activeChallenges,
        completedChallenges: state.completedChallenges,
        joinedLeaderboard: state.joinedLeaderboard,
        lastSeenStage: state.lastSeenStage,
        skippedLetterStages: state.skippedLetterStages,
      }),
    }
  )
);
